import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getFamilySubscriptionStatuses, FamilySubscriptionStatus } from '../services/api/familypal/config';

export interface FamilySubscriptionInfo {
  familyId: string;
  familyName: string;
  status: string;
  isActive: boolean;
}

interface SubscriptionContextValue {
  status: string;
  isSubscribed: boolean;
  isLoading: boolean;
  isTrial: boolean;
  expiresAt: string | null;
  platform: string | null;
  familySubscriptions: FamilySubscriptionInfo[];
  refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [familySubscriptions, setFamilySubscriptions] = useState<FamilySubscriptionInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch all family subscription statuses — no family ID needed, backend uses user context
      const familyStatuses = await getFamilySubscriptionStatuses();

      const mapped = familyStatuses.map((fs) => ({
        familyId: fs.familyId || fs.family_id || '',
        familyName: fs.familyName || fs.family_name || '',
        status: fs.status,
        isActive: fs.status === 'active' || fs.status === 'billing_retry',
      }));

      setFamilySubscriptions(mapped);
    } catch (err) {
      console.error('[SubscriptionContext] Fetch error:', err);
      setFamilySubscriptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // User has access if ANY of their families has an active subscription
  const isSubscribed = familySubscriptions.some((fs) => fs.isActive);
  const status = isSubscribed ? 'active' : 'none';

  const value: SubscriptionContextValue = {
    status,
    isSubscribed,
    isLoading,
    isTrial: false,
    expiresAt: null,
    platform: null,
    familySubscriptions,
    refetch: fetchStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextValue => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
